/* eslint-disable import/no-mutable-exports -- we mutate our exported function after it has been initialized on purpose in order to all getPreferences to be synchronous */

import {
  featureFlagFactory,
  controlFeatureFlag,
  updateFlagOverrides,
  updateMockFlags,
  resetFeatureFlagFactory,
} from '@fs/flags-js/react'
import type {
  FactoryResult,
  FeatureAttributes,
  FeatureFlag,
  FeatureFlags,
  Flag,
  Flags,
  StandardAttributes,
} from '@fs/flags-js'
import zionDebug from '@fs/zion-debug'

const debug = zionDebug('zion:flags:flagsJsHelper')

export let getFeatureFlag: (flagName: string, attributes?: FeatureAttributes) => FeatureFlag<Flag<object>> =
  preInitializedGetFeatureFlag
export let getFeatureFlags: (flagNames: string[], attributes?: FeatureAttributes) => FeatureFlags<Flags> =
  preInitializedGetFeatureFlags

let mocksSet = false
let flagsReadyResolve: ((value: FactoryResult | PromiseLike<FactoryResult> | void) => void) | undefined
let flagsReadyPromise = new Promise<FactoryResult>((resolve) => {
  flagsReadyResolve = resolve
})

export async function flagsAreReady(
  mockFlags: Flags = undefined,
  flagOverrides: Flags = undefined,
  standardAttributes: StandardAttributes = undefined
): Promise<FactoryResult | undefined> {
  debug('flagsAreReady: start with mockFlags:', mockFlags, ' and overrides: ', flagOverrides)
  if (flagsReadyPromise && mocksSet) {
    debug(
      'flagsAreReady: localMockFlags and localFlagOverrides already set, returning existing promise ...',
      flagsReadyPromise
    )
    if (mockFlags) {
      await updateMockFlags(mockFlags)
    }
    if (flagOverrides) {
      updateFlagOverrides(flagOverrides)
    }
  } else {
    if (!flagsReadyPromise) {
      flagsReadyPromise = new Promise<FactoryResult>((resolve) => {
        flagsReadyResolve = resolve
      })
    }
    // this will get called multiple times, but we need to wait until FeatureFlagsProvider calls with mockFlags and flagOverrides to accurately evaluate a flag
    if (mockFlags !== undefined || flagOverrides !== undefined) {
      debug('flagsAreReady: received mockFlags or flagOverrides, now instantiating flags-js factory and clients')
      mocksSet = true

      const factoryPromise = featureFlagFactory(
        mockFlags ? {} : standardAttributes,
        undefined,
        mockFlags,
        flagOverrides
      )
      factoryPromise.then(({ getFeatureFlag: realGetFeatureFlag, getFeatureFlags: realGetFeatureFlags }) => {
        getFeatureFlag = realGetFeatureFlag
        getFeatureFlags = realGetFeatureFlags
        debug('flagsAreReady: true, flags-js factory and clients are now initialized', getFeatureFlag, getFeatureFlags)
      })

      factoryPromise
        .then((factoryResult) => {
          if (factoryResult) {
            debug('flagsAreReady: factory resolved with:', factoryResult)
            flagsReadyResolve(factoryResult)
          } else {
            debug('flagsAreReady: factory resolved with no result')
            flagsReadyResolve()
          }
        })
        .catch((error) => {
          debug('flagsAreReady: factory failed, falling back to control behavior:', error)
          flagsReadyResolve()
        })
    } else {
      debug('flagsAreReady: waiting to receive mockFlags or flagOverrides ...')
    }
  }

  debug('flagsAreReady: returning flagsReadyPromise', flagsReadyPromise)
  return flagsReadyPromise
}

function preInitializedGetFeatureFlag(flagName: string): FeatureFlag<Flag<object>> {
  console.error(
    `zion-flags Warning: Attempted to getFeatureFlag ${flagName} before a connection was established with splitio.
    Returning the control treatment, but you should make sure you are using getFeatureFlag inside of a render`
  )

  return controlFeatureFlag
}

function preInitializedGetFeatureFlags(flagNames: string[]): FeatureFlags<Flags> {
  console.error(
    `zion-flags Warning: Attempted to getFeatureFlags [${flagNames.join(
      ', '
    )}] before a connection was established with splitio.
    Returning the control treatment, but you should make sure you are using getFeatureFlags inside of a render`
  )

  return flagNames.reduce((acc, flagName) => {
    acc[flagName] = controlFeatureFlag
    return acc
  }, {})
}

export async function resetFeatureFlags(): Promise<void> {
  await resetFeatureFlagFactory()
  mocksSet = false
  flagsReadyResolve = undefined // Clear reference to prevent memory leak
  flagsReadyPromise = new Promise<FactoryResult>((resolve) => {
    flagsReadyResolve = resolve
  })
}
